import React, { useState, useRef, useEffect } from 'react';

interface Props {
  opciones: string[];
  valor: string;
  onChange: (val: string) => void;
  placeholder?: string;
}

const Autocomplete: React.FC<Props> = ({ opciones, valor, onChange, placeholder }) => {
  const [mostrar, setMostrar] = useState(false);
  const [destacado, setDestacado] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listaRef = useRef<HTMLUListElement>(null);

  const query = valor.trim().toLowerCase();
  const filtradas = query.length >= 1
    ? opciones
      .map(op => {
        const normalized = op.trim().toLowerCase();
        let score = 99;
        if (normalized === query) score = 0;
        else if (normalized.startsWith(query)) score = 1;
        else if (normalized.includes(` ${query}`)) score = 2;
        else if (normalized.includes(query)) score = 3;
        return { op: op.trim(), score };
      })
      .filter(x => x.score < 99)
      .sort((a, b) => (a.score - b.score) || (a.op.length - b.op.length) || a.op.localeCompare(b.op))
      .slice(0, 12)
      .map(x => x.op)
    : [];

  const seleccionar = (op: string) => {
    onChange(op);
    setMostrar(false);
    setDestacado(-1);
  };

  const manejarTecla = (e: React.KeyboardEvent) => {
    if (!mostrar || filtradas.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDestacado(prev => Math.min(prev + 1, filtradas.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDestacado(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && destacado >= 0) {
      e.preventDefault();
      seleccionar(filtradas[destacado]);
    } else if (e.key === 'Escape') {
      setMostrar(false);
    }
  };

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(e.target as Node) &&
          listaRef.current && !listaRef.current.contains(e.target as Node)) {
        setMostrar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        className="w-full rounded-lg border-2 px-4 py-3 text-[#d8fdf0] placeholder-[#8acfb6] focus:outline-none"
        style={{
          background: 'linear-gradient(135deg, rgba(10, 10, 15, 0.96), rgba(22, 33, 62, 0.92), rgba(10, 10, 15, 0.96))',
          borderColor: 'rgba(0, 255, 136, 0.55)',
          boxShadow: '0 0 14px rgba(0, 255, 136, 0.22), inset 0 0 18px rgba(0, 255, 136, 0.08), 0 0 20px rgba(0, 255, 255, 0.14)',
        }}
        placeholder={placeholder}
        value={valor}
        onChange={e => { onChange(e.target.value); setMostrar(true); setDestacado(-1); }}
        onKeyDown={manejarTecla}
        onFocus={() => valor.trim().length >= 1 && setMostrar(true)}
        autoComplete="off"
      />
      {mostrar && filtradas.length > 0 && (
        <ul
          ref={listaRef}
          className="absolute z-50 w-full mt-1 rounded-lg overflow-hidden shadow-xl max-h-60 overflow-y-auto"
          style={{
            background: 'linear-gradient(180deg, rgba(8, 12, 25, 0.98), rgba(16, 22, 44, 0.97))',
            border: '2px solid rgba(0, 255, 136, 0.45)',
            boxShadow: '0 0 16px rgba(0, 255, 136, 0.25), 0 0 24px rgba(0, 255, 255, 0.12)',
          }}
        >
          {filtradas.map((op, i) => (
            <li
              key={i}
              onMouseDown={() => seleccionar(op)}
              className={`px-4 py-3 font-main text-base cursor-pointer transition-colors ${
                i === destacado
                  ? 'text-[#e9fff6]'
                  : 'text-[#c9f7e5]'
              }`}
              style={
                i === destacado
                  ? {
                      background: 'linear-gradient(90deg, rgba(0, 255, 136, 0.26), rgba(0, 255, 255, 0.18))',
                      textShadow: '0 0 6px rgba(0, 255, 136, 0.35)',
                    }
                  : { background: 'transparent' }
              }
            >
              {op}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Autocomplete;
